<template>
  <v-container class="chat-history-panel black--text pa-0">
    <v-row
      no-gutters
      align="center"
      justify="space-between"
      class="chat-history-header mb-2"
    >
      <div class="pl-4 text-h5">{{ $t('aiChat.chatHistory') }}</div>
      <div>
        <v-menu offset-y content-class="user-list-menu">
          <template v-slot:activator="{ on }">
            <v-btn icon small v-on="on" class="round-btn">
              <v-icon color="grey">mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list light>
            <v-list-item
              :disabled="groupedChatHistory.length === 0"
              @click="toggleClearAllDialog"
            >
              <v-list-item-title>
                {{ $t('aiChat.clearAllChat') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn icon @click="handleHistoryClick" aria-label="hide button">
          <v-icon color="grey">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-row>
    <v-divider></v-divider>
    <v-row no-gutters v-if="groupedChatHistory.length > 0">
      <ChatHistoryList
        :groupedChatHistory="groupedChatHistory"
        :showSpinner="showSpinner"
        @selectHistory="selectHistory"
        @deleteHistory="handleDeleteHistory"
        @editHistory="handleEditHistory"
        @loadMoreHistory="loadMoreHistory"
      />
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <div class="text-center">{{ $t('aiChat.noChatHistory') }}</div>
      </v-col>
    </v-row>
    <v-dialog
      v-model="hideClearAllDialog"
      v-if="hideClearAllDialog"
      max-width="450px"
    >
      <v-card height="210">
        <v-card-title class="headline">{{ deleteDialogTitle }}</v-card-title>
        <v-card-text>
          {{ deleteDialogText }}
        </v-card-text>
        <v-card-actions>
          <v-row>
            <v-col cols="12" class="d-flex justify-space-between">
              <v-btn @click="onHideClearAllDialog" :disabled="clearing">{{
                $t('global.close')
              }}</v-btn>
              <v-btn
                color="secondary"
                @click="submitDeleteHistory"
                :disabled="clearing"
                :loading="clearing"
                >{{ $t('global.delete') }}</v-btn
              >
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapActions } from 'vuex'
import ChatHistoryList from './ChatHistoryList.vue'

export default defineComponent({
  name: 'ChatHistoryPanel',
  components: {
    ChatHistoryList,
  },
  props: {
    groupedChatHistory: {
      type: Array,
      default: () => [],
    },
    vSessionId: {
      type: String,
      default: '',
    },
    showSpinner: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hideClearAllDialog: false,
      clearing: false,
      deleteEntry: null,
    }
  },
  computed: {
    deleteDialogTitle() {
      return this.deleteEntry
        ? this.$t('aiChat.sureDeleteChat')
        : this.$t('aiChat.sureClearChat')
    },
    deleteDialogText() {
      return this.deleteEntry
        ? `${this.$t('global.delete')} ${this.deleteEntry.title}. ${this.$t(
            'aiChat.deleteChatText'
          )}`
        : this.$t('aiChat.clearChatText')
    },
  },
  methods: {
    ...mapActions('Chat', [
      'historyDeleteAll',
      'deleteAllChatHistory',
      'historyDelete',
      'deleteChatEntry',
      'historyRename',
      'updateChatTitle',
    ]),
    ...mapActions('ToastMessage', ['callFailureToast']),
    async submitDeleteHistory() {
      if (this.deleteEntry) {
        await this.deleteChatHistory()
      } else {
        await this.onClearAllChatHistory()
      }
    },
    async deleteChatHistory() {
      this.clearing = true
      const response = await this.historyDelete({
        conversation_id: this.deleteEntry.id,
        config: { vSessionId: this.vSessionId },
      })
      if (response.ok) {
        this.deleteChatEntry(this.deleteEntry.id)
        this.selectHistory({ messages: [] })
        this.onHideClearAllDialog()
      }
      this.clearing = false
    },
    loadMoreHistory() {
      this.$emit('loadMoreHistory')
    },
    async onClearAllChatHistory() {
      this.clearing = true
      const response = await this.historyDeleteAll({
        config: { vSessionId: this.vSessionId },
      })
      if (response.ok) {
        this.deleteAllChatHistory()
        this.selectHistory({ messages: [] })
        this.onHideClearAllDialog()
      } else {
        this.callFailureToast({
          toastMessage: this.$t('aiChat.networkError'),
        })
      }
      this.clearing = false
    },
    handleDeleteHistory(entry: any) {
      this.deleteEntry = entry
      this.hideClearAllDialog = true
    },
    async handleEditHistory(entry: any) {
      const response = await this.historyRename({
        conversation_id: entry.id,
        title: entry.title,
        config: { vSessionId: this.vSessionId },
      })
      if (response.ok) {
        this.updateChatTitle(entry)
      } else {
        this.callFailureToast({
          toastMessage: this.$t('aiChat.networkError'),
        })
      }
    },
    onHideClearAllDialog() {
      this.deleteEntry = null
      this.hideClearAllDialog = false
    },
    toggleClearAllDialog() {
      this.hideClearAllDialog = true
    },
    handleHistoryClick() {
      this.$emit('hideHistory')
    },
    selectHistory(entry: any) {
      this.$emit('selectHistory', entry)
    },
  },
  mounted() {},
})
</script>

<style lang="scss">
.chat-history-panel {
  flex: 0 1 auto;
  width: 40%;
}
</style>
